// Uygulama içi PDF görüntüleyici.
//
// Neden iframe değil: iOS Safari, iframe içindeki PDF'i yalnızca ilk sayfa
// olarak, kaydırılamaz biçimde çiziyor. Sözleşme telefonda sonuna kadar
// okunacağı için bu kullanılamaz. PDF.js sayfaları canvas'a çizer; her
// tarayıcıda aynı, akıcı ve kaydırılabilir olur.

declare const pdfjsLib: any;

const CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/';

function ready(): boolean {
  if (typeof pdfjsLib === 'undefined') return false;
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = CDN + 'pdf.worker.min.js';
  }
  return true;
}

export type PdfHandle = { pages: number; destroy: () => void };

/**
 * `bytes` içindeki PDF'i `host` içine sayfa sayfa çizer.
 * onProgress(görülen, toplam) — sonuna kadar okundu kontrolü için.
 */
export async function renderPdf(
  host: HTMLElement,
  bytes: Uint8Array,
  opts: { onLastPageSeen?: () => void } = {},
): Promise<PdfHandle> {
  if (!ready()) throw new Error('PDF görüntüleyici yüklenemedi, bağlantınızı kontrol edin');

  host.textContent = '';
  host.classList.add('pdfview');

  // getDocument veriyi devraldığı için kopya veriyoruz; çağıran taraf aynı
  // baytları imzalama sırasında yeniden kullanıyor.
  const doc = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let lastSeen = false;

  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const wrap = document.createElement('div');
    wrap.className = 'pdfview-page';
    const canvas = document.createElement('canvas');
    wrap.appendChild(canvas);
    host.appendChild(wrap);

    const base = page.getViewport({ scale: 1 });
    const cssWidth = host.clientWidth || 320;
    const scale = cssWidth / base.width;
    const vp = page.getViewport({ scale: scale * dpr });

    canvas.width = Math.floor(vp.width);
    canvas.height = Math.floor(vp.height);
    canvas.style.width = '100%';
    canvas.style.height = 'auto';

    await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise;

    if (n === doc.numPages && opts.onLastPageSeen) {
      // Son sayfa ekrana girdiğinde "sonuna kadar okundu" sayılır.
      const io = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting) && !lastSeen) {
          lastSeen = true;
          opts.onLastPageSeen!();
          io.disconnect();
        }
      }, { root: host.closest('.sheet-body') || host.parentElement, threshold: 0.35 });
      io.observe(wrap);
    }
  }

  return {
    pages: doc.numPages,
    destroy: () => { try { doc.destroy(); } catch { /* yoksay */ } },
  };
}

/**
 * Belgeyi uygulama içinde gösterir: PDF sayfa sayfa canvas'a, görsel img
 * olarak. Yeni sekme açılmaz — adres çubuğunda blob URL görünmez ve iOS
 * Safari'nin PDF sorununa takılmayız.
 */
export async function viewFileInApp(
  host: HTMLElement,
  blob: Blob,
  name: string,
): Promise<void> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const isPdf = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
  if (isPdf) {
    await renderPdf(host, bytes);
    return;
  }
  const isImage = (bytes[0] === 0x89 && bytes[1] === 0x50)          // PNG
    || (bytes[0] === 0xFF && bytes[1] === 0xD8);                     // JPEG
  host.textContent = '';
  if (isImage) {
    const url = URL.createObjectURL(blob);
    const img = document.createElement('img');
    img.alt = name;
    img.src = url;
    img.className = 'fileview-img';
    img.onload = () => setTimeout(() => URL.revokeObjectURL(url), 60_000);
    host.appendChild(img);
    return;
  }
  const p = document.createElement('p');
  p.className = 'text-muted';
  p.textContent = 'Bu dosya türü önizlenemiyor. İndirerek açabilirsiniz.';
  host.appendChild(p);
}
